import config from '../config';
import api from '../utils/api';
import { FileItem } from '../types/files';

function getResponseHeader(headers: Record<string, unknown>, name: string): string | undefined {
  const value = headers[name];
  if (typeof value === 'string') return value;
  if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
  return undefined;
}

// Use config for API base URL instead of hardcoding
const API_BASE_URL = config.server.baseUrl;

export interface FileResponse {
  items: FileItem[];
  total: number;
}

export interface FileVersion {
  id: string;
  version: number;
  createdAt: string;
  createdBy: string;
  size: number;
  hash: string;
  isCurrent: boolean;
}

export interface LockStatusResponse {
  lockStatus: 'locked' | 'unlocked';
  lockedBy?: {
    id: string;
    name: string;
  };
  lockedAt?: string;
  expiresAt?: string;
}

export type FileDetails = {
  id: string;
  name: string;
  type: 'file' | 'folder';
  mimetype?: string;
  extension?: string;
  size?: number;
  createdAt: string;
  updatedAt: string;
  ownerId: string;
  owner: {
    id: string;
    name: string;
  };
  path: string;
  versions?: FileVersion[];
  lockStatus?: 'locked' | 'unlocked';
  lockedBy?: {
    id: string;
    name: string;
  };
  lockedAt?: string;
  expiresAt?: string;
  [key: string]: unknown; // Allow additional properties
};

export interface CompressionOptions {
  quality?: number;
  format?: string;
  width?: number;
  height?: number;
}

export interface OCRSettings {
  language?: string;
  enhanceImage?: boolean;
  detectOrientation?: boolean;
  createSearchableFile?: boolean;
}

export interface OCRResponse {
  text: string;
  success: boolean;
  error?: string;
}

// Add type definitions for sort and order
export type FileSortField = 'name' | 'size' | 'createdAt' | 'updatedAt';
export type SortOrder = 'asc' | 'desc';

export interface UploadProgress {
  progress: number;
  status: 'uploading' | 'completed' | 'error';
  error?: string;
}

export interface UploadOptions {
  folderId?: string;
  onProgress?: (progress: UploadProgress) => void;
}

class FileService {
  // Use the api instance with auth interceptors instead of creating a new instance
  private api = api;

  // Get files with optional filtering
  async getFiles(parentIdOrFilters: string | Record<string, any>): Promise<FileResponse> {
    const isParentId = typeof parentIdOrFilters === 'string';

    try {
      let url = '/files';

      if (isParentId) {
        // It's a parent ID (folder ID)
        url = `/files?parentId=${parentIdOrFilters}`;
      } else {
        // It's a filters object
        const filters = parentIdOrFilters;
        const queryParams = new URLSearchParams();
        
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            queryParams.append(key, String(value));
          }
        });
        
        url = `/files?${queryParams.toString()}`;
      }

      const response = await api.get(url, {
        timeout: 10000, // 10 seconds timeout
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Upload a single file
  async uploadFile(file: File, options?: UploadOptions): Promise<FileItem> {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.folderId) {
      formData.append('folderId', options.folderId);
    }

    try {
      const response = await this.api.post('/files/upload', formData, {
        timeout: config.upload.requestTimeoutMs,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (options?.onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            options.onProgress({
              progress,
              status: 'uploading',
            });
          }
        },
      });
      if (options?.onProgress) {
        options.onProgress({
          progress: 100,
          status: 'completed',
        });
      }

      return response.data;
    } catch (error: any) {
      if (options?.onProgress) {
        options.onProgress({
          progress: 0,
          status: 'error',
          error: error.response?.data?.message || error.message || 'Upload failed',
        });
      }
      throw error;
    }
  }

  // Upload multiple files
  async uploadFiles(files: File[], options?: UploadOptions): Promise<FileItem[]> {
    const uploads = files.map(file => this.uploadFile(file, options));
    return Promise.all(uploads);
  }

  // Create a new folder
  async createFolder(name: string, parentFolderId?: string): Promise<FileItem> {
    const response = await this.api.post('/folders/create', { name, parentFolderId });
    return response.data;
  }

  // Delete a file or folder
  async deleteItem(id: string): Promise<void> {
    await this.api.delete(`/files/${id}`);
  }

  async restoreFile(id: string): Promise<void> {
    await this.api.post(`/files/${id}/restore`);
  }

  async deleteFilePermanent(id: string): Promise<void> {
    await this.api.delete(`/files/${id}`, { params: { permanent: true } });
  }

  // Move files/folders using bulk operation
  async moveItems(itemIds: string[], targetFolderId: string | undefined): Promise<void> {
    await this.api.post('/files/bulk-operation', {
      operation: 'move',
      fileIds: itemIds,
      destinationId: targetFolderId
    });
  }

  // Copy files/folders using bulk operation
  async copyItems(fileIds: string[], targetFolderId: string | undefined): Promise<void> {
    await this.api.post('/files/bulk-operation', {
      operation: 'copy',
      fileIds,
      destinationId: targetFolderId
    });
  }

  // Copy a single file using bulk operation
  async copyFile(id: string, targetFolderId: string | undefined): Promise<void> {
    await this.copyItems([id], targetFolderId);
  }

  // Rename a file
  async renameFile(id: string, newName: string): Promise<FileItem> {
    const response = await this.api.patch(`/files/${id}/rename`, { name: newName });
    return response.data;
  }

  // Update file/folder metadata
  async updateItem(id: string, updates: Partial<FileItem>): Promise<FileItem> {
    const response = await this.api.patch(`/files/${id}`, updates);
    return response.data;
  }

  // Download a file
  async downloadFile(id: string): Promise<void> {
    try {
      const response = await this.api.get(`/files/${id}/download`, {
        responseType: 'blob',
      });
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = getResponseHeader(response.headers, 'content-disposition');
      let filename = 'download';
      
      if (contentDisposition) {
        // Try to get filename from RFC 5987 format (filename*=UTF-8''...)
        const utf8FilenameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
        if (utf8FilenameMatch && utf8FilenameMatch[1]) {
          filename = decodeURIComponent(utf8FilenameMatch[1]);
        } else {
          // Fall back to regular filename="..." format
          const regularFilenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/i);
          if (regularFilenameMatch && regularFilenameMatch[1]) {
            filename = regularFilenameMatch[1].trim();
          }
        }
      }
      
      console.log('Downloading file:', filename, 'Content-Disposition:', contentDisposition);
      
      // Get content type from response headers
      const contentType = getResponseHeader(response.headers, 'content-type') || 'application/octet-stream';
      
      // Create blob with proper content type
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      throw error;
    }
  }

  // Share a file/folder
  async shareItem(id: string, shareOptions: {
    email?: string;
    permission: 'view' | 'edit';
    expiresAt?: string;
  }): Promise<{ shareUrl: string }> {
    const response = await this.api.post(`/shares/file/${id}`, {shareType: 'view', expiration: shareOptions.expiresAt});
    return {shareUrl: response.data.shareLink.url};
  }

  // Get file preview URL with optional optimization
  getPreviewUrl(id: string, format?: string, options?: { mode?: 'thumbnail' | 'preview' | 'library'; optimize?: boolean; width?: number; height?: number; quality?: number }): string {
    const params = new URLSearchParams();
    
    if (format) {
      params.append('format', format);
    }
    
    // Add optimization options
    if (options) {
      if (options.mode) {
        params.append('mode', options.mode);
      }
      if (options.optimize !== undefined) {
        params.append('optimize', options.optimize.toString());
      } else {
        // Default to optimized for images
        params.append('optimize', 'true');
      }
      if (options.width) {
        params.append('width', options.width.toString());
      }
      if (options.height) {
        params.append('height', options.height.toString());
      }
      if (options.quality) {
        params.append('quality', options.quality.toString());
      }
    } else {
      // Default to optimized preview if no options provided
      params.append('optimize', 'true');
      params.append('mode', 'preview');
    }
    
    return `${API_BASE_URL}/files/${id}/preview?${params.toString()}`;
  }

  async getPreviewAccessToken(fileId: string): Promise<{ token: string; expiresIn: number; queryParam: string }> {
    const response = await this.api.get(`/files/${fileId}/preview-token`);
    return response.data;
  }

  /**
   * Preview URL with optional short-lived previewToken for tags that cannot send Authorization (img, video, iframe).
   */
  async buildPreviewUrl(
    id: string,
    format?: string,
    options?: { mode?: 'thumbnail' | 'preview' | 'library'; optimize?: boolean; width?: number; height?: number; quality?: number },
    opts?: { withAccessToken?: boolean; versionId?: string }
  ): Promise<string> {
    const params = new URLSearchParams();
    if (format) {
      params.append('format', format);
    }
    if (options) {
      if (options.mode) params.append('mode', options.mode);
      if (options.optimize !== undefined) {
        params.append('optimize', options.optimize.toString());
      } else {
        params.append('optimize', 'true');
      }
      if (options.width) params.append('width', options.width.toString());
      if (options.height) params.append('height', options.height.toString());
      if (options.quality) params.append('quality', options.quality.toString());
    } else {
      params.append('optimize', 'true');
      params.append('mode', 'preview');
    }
    if (opts?.versionId) {
      params.append('versionId', opts.versionId);
    }
    const useToken = opts?.withAccessToken !== false;
    if (useToken) {
      const {token, queryParam} = await this.getPreviewAccessToken(id);
      if (!token) throw new Error('Preview authorization is unavailable');
      params.append(queryParam || 'previewToken', token);
    }
    return `${API_BASE_URL}/files/${id}/preview?${params.toString()}`;
  }

  // Get file thumbnail URL
  getThumbnailUrl(id: string): string {
    return `${API_BASE_URL}/files/${id}/thumbnail`;
  }

  // Get folder contents
  async getFolderContents(folderId: string, params?: {
    search?: string;
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
    tags?: string[];
    page?: number;
    limit?: number;
  }): Promise<{ items: FileItem[]; total: number }> {
    const response = await this.api.get(`/folders/${folderId}/contents`, { params });
    return response.data;
  }

  // Get breadcrumb path for a folder
  async getFolderPath(folderId: string): Promise<FileItem[]> {
    const response = await this.api.get(`/folders/${folderId}/path`);
    return response.data;
  }

  // Get a single file
  async getFile(id: string): Promise<FileDetails> {
    const response = await this.api.get(`/files/${id}`);
    return response.data.file ?? response.data;
  }

  // Update file metadata
  async updateFile(id: string, updates: Partial<FileItem>): Promise<FileItem> {
    return this.updateItem(id, updates);
  }

  // Move file to a new folder
  async moveFile(id: string, targetFolderId: string | undefined): Promise<void> {
    await this.moveItems([id], targetFolderId);
  }

  // Delete file
  async deleteFile(id: string): Promise<void> {
    return this.deleteItem(id);
  }

  // Get file versions
  async getFileVersions(id: string): Promise<{ versions: FileVersion[] }> {
    const response = await this.api.get(`/files/${id}/versions`);
    return response.data;
  }

  // Restore file version
  async restoreFileVersion(fileId: string, versionId: string): Promise<void> {
    await this.api.post(`/files/${fileId}/versions/${versionId}/restore`);
  }

  // Delete file version
  async deleteFileVersion(fileId: string, versionId: string): Promise<void> {
    await this.api.delete(`/files/${fileId}/versions/${versionId}`);
  }

  // Download file version
  async downloadFileVersion(fileId: string, versionId: string): Promise<void> {
    const response = await this.api.get(`/files/${fileId}/versions/${versionId}/download`, {responseType: 'blob'});
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url; link.download = `version-${versionId}`;
    document.body.appendChild(link); link.click(); link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  // Get file lock status
  async getFileLockStatus(fileId: string): Promise<LockStatusResponse> {
    const response = await this.api.get(`/files/${fileId}/lock-status`);
    return response.data;
  }

  // Lock file
  async lockFile(fileId: string, options?: { comment?: string; expiresAt?: string }): Promise<void> {
    await this.api.post(`/files/${fileId}/lock`, options || {});
  }

  // Unlock file
  async unlockFile(fileId: string): Promise<void> {
    await this.api.delete(`/files/${fileId}/lock`);
  }

  // Compress file
  async compressFile(fileId: string, options: CompressionOptions): Promise<{ success: boolean; url?: string }> {
    const response = await this.api.post(`/files/${fileId}/compress`, options);
    return response.data;
  }

  // Process OCR
  async processOCR(fileId: string, settings: OCRSettings): Promise<OCRResponse> {
    const response = await this.api.post(`/files/${fileId}/ocr`, settings);
    return response.data;
  }

  // Get file by ID
  async getFileById(fileId: string): Promise<File> {
    const response = await this.api.get(`/files/${fileId}`);
    return response.data;
  }

  // Update file metadata
  async updateFileMetadata(fileId: string, metadata: Record<string, any>): Promise<void> {
    await this.api.put(`/files/${fileId}/metadata`, metadata);
  }
}

export const fileService = new FileService();
export default fileService; 